import React from 'react';
import Box from '@mui/material/Box';
import { styled, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import JsonView, { stringify as stringifyForCopying } from 'react18-json-view';
import type { Collapsed, Editable, CustomizeNode, DisplaySize } from 'react18-json-view/dist/types';
import 'react18-json-view/src/style.css';
import 'react18-json-view/src/dark.css';
import mergeSxProps from '../shared/mergeSxProps';

const BoxStyled = styled(Box)(() => ({
  '&.MuiBox-root': {
    width: '100% !important',
    flexBasis: '100% !important',
    overflow: 'hidden',
  },
}));

export type CollapsedParams = {
  node: Record<string, unknown> | Array<unknown>;
  indexOrName: number | string | undefined;
  depth: number;
  size: number;
};

const formatDatesAsIso: CustomizeNode = ({ node }) => {
  if (node instanceof Date) {
    return <span className="json-view--string">"{node.toISOString()}"</span>;
  }
  return undefined;
};

export interface JsonTreeProps {
  rootClass?: string;
  src: unknown;
  collapseStringsAfterLength?: number;
  collapseStringMode?: 'directly' | 'word' | 'address';
  collapseObjectsAfterLength?: number;
  collapsed?: Collapsed;
  enableClipboard?: boolean;
  editable?: Editable;
  customizeNode?: CustomizeNode;
  customizeCopy?: (node: unknown) => unknown;
  dark?: boolean;
  theme?: 'default' | 'a11y' | 'github' | 'vscode' | 'atom' | 'winter-is-coming';
  backgroundColor?: string;
  displaySize?: DisplaySize;
  sx?: SxProps<Theme>;
  style?: React.CSSProperties;
  className?: string;
  matchesURL?: boolean;
  urlRegExp?: RegExp;
}

const JsonTree = ({
  src,
  theme = 'default',
  backgroundColor,
  collapsed = 1,
  collapseStringsAfterLength = 80,
  customizeNode,
  customizeCopy = (node: unknown) =>
    typeof node === 'string' ? node : stringifyForCopying(node, 2),
  rootClass,
  style,
  sx,
  ...rest
}: JsonTreeProps) => {
  const muiTheme = useTheme<Theme>();
  const backgroundColors = {
    default: muiTheme.palette.background.admin,
    a11y: '#2B2B2B',
    github: '#24292E',
    vscode: '#1E1E1E',
    atom: '#282C34',
    'winter-is-coming': '#011627',
  };
  backgroundColor = backgroundColor || backgroundColors[theme] || backgroundColors.default;

  // Compose customizeNode to apply date formatting first, then user's customization
  const composedCustomizeNode: CustomizeNode = (params) => {
    const dateResult = formatDatesAsIso(params);
    if (dateResult !== undefined) return dateResult;
    return customizeNode?.(params);
  };

  return (
    <BoxStyled className={clsx('json-tree', rootClass)} sx={mergeSxProps({ backgroundColor }, sx)}>
      <JsonView
        {...rest}
        src={src}
        collapsed={collapsed}
        collapseStringsAfterLength={collapseStringsAfterLength}
        customizeNode={composedCustomizeNode}
        customizeCopy={customizeCopy}
        style={{ fontSize: '0.8rem', ...style }}
      />
    </BoxStyled>
  );
};

JsonTree.displayName = 'JsonTree';

export default JsonTree;
